import { FC, ReactElement, ReactNode, useMemo } from 'react';
import { Chip, ChipProps, SxProps, Theme, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { borderRadius } from '../config';

export type BadgeVariant =
  | 'neutral'
  | 'error'
  | 'successSoft'
  | 'warningSoft'
  | 'Completed'
  | 'Processing'
  | 'Draft'
  | 'Submitted'
  | 'Failed'
  | 'DomainName'
  | 'Required'
  | 'Optional'
  | 'Language';

export interface BadgeProps {
  label: ReactNode;
  variant?: BadgeVariant;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
  chipProps?: Omit<ChipProps, 'label' | 'icon' | 'size' | 'sx' | 'variant'>;
}

// You may need to adjust the style if you want to add icon
const getVariantStyles = (variant: BadgeVariant, theme: Theme): SxProps<Theme> => {
  const variantStyles: Record<BadgeVariant, SxProps<Theme>> = {
    Required: {
      backgroundColor: theme.palette.orange[100],
      color: theme.palette.orange[500],
      border: `1px solid ${theme.palette.orange[200]}`,
      fontWeight: 600,
      fontSize: '12px',
    },
    DomainName: {
      backgroundColor: theme.palette.blue[300],
      color: theme.palette.blue[700],
      border: `1px solid ${theme.palette.blue[800]}`,
      fontWeight: 600,
    },
    error: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.background.paper,
      border: `1px solid ${theme.palette.error.main}`,
    },
    neutral: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.grey[400]}`,
      fontWeight: 500,
    },
    // Tinted counterparts to the solid `Completed` / `Submitted` pills, for
    // rows where a saturated block of colour would dominate the table.
    successSoft: {
      backgroundColor: theme.palette.green[200],
      color: theme.palette.green[800],
      border: `1px solid ${theme.palette.green[300]}`,
      fontWeight: 600,
    },
    warningSoft: {
      backgroundColor: theme.palette.yellow[200],
      color: theme.palette.yellow[900],
      border: `1px solid ${theme.palette.yellow[400]}`,
      fontWeight: 600,
    },
    Completed: {
      backgroundColor: theme.palette.accent.green,
      color: theme.palette.background.paper,
      border: `1px solid ${theme.palette.green[800]}`,
    },
    Processing: {
      backgroundColor: theme.palette.accent.blue,
      color: theme.palette.background.paper,
      border: `1px solid ${theme.palette.blue[800]}`,
    },
    Draft: {
      backgroundColor: theme.palette.black[400],
      color: theme.palette.background.paper,
      border: `1px solid ${theme.palette.text.secondary}`,
    },
    Submitted: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.background.paper,
      border: `1px solid ${theme.palette.yellow[800]}`,
    },
    Failed: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.background.paper,
      border: `1px solid ${theme.palette.red[600]}`,
    },
    Optional: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.grey[400]}`,
      fontWeight: 500,
      fontSize: '12px',
    },
    Language: {
      backgroundColor: '#F2F2F2',
      color: theme.palette.black[400],
      fontWeight: 600,
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      borderRadius: '6px',
    },
  };
  return variantStyles[variant];
};

export const Badge: FC<BadgeProps> = observer(
  ({ label, variant = 'neutral', icon, sx, chipProps }) => {
    const theme = useTheme();

    const sxStyles = useMemo(
      () => [
        {
          borderRadius: borderRadius.sm,
          fontWeight: 500,
          fontSize: '0.75rem',
          // Don't let the chip itself shrink below its label, and don't
          // ellipsize the label — status pills like "Completed" should always
          // render in full, even in narrow table cells.
          flexShrink: 0,
          maxWidth: 'none',
          '& .MuiChip-label': {
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'nowrap',
          },
          ...getVariantStyles(variant, theme),
          ...(icon && {
            '& .MuiChip-icon': {
              marginInlineStart: '6px',
              marginInlineEnd: '4px',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                width: '100%',
                height: '100%',
                fontSize: '16px',
              },
            },
          }),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ],
      [variant, theme, icon, sx],
    );

    return (
      <Chip
        label={label}
        icon={
          icon && typeof icon === 'object' && 'type' in icon ? (icon as ReactElement) : undefined
        }
        size="small"
        variant="filled"
        sx={sxStyles}
        {...chipProps}
      />
    );
  },
);
